import { useAuth } from './useAuth';
import { useHealth } from './useHealth';

/** The `guardian_role` value granted to Scality staff (mirrors the backend `UserRole.INTERNAL`). */
const INTERNAL_ROLE = 'Internal';

/** The backend's `ENVIRONMENT` on demo (on-demand) envs, as reported by `/health`. */
const DEMO_ENVIRONMENT = 'demo';

/**
 * True when the authenticated user has the Internal Guardian role. Internal-only features
 * (e.g. dashboards) gate on this. A missing/unknown role is treated as external.
 *
 * Demo envs authenticate against Dex with the `openid profile email` scope, so their tokens
 * carry no `guardian_role` at all — everyone is internal there. The environment comes from
 * `/health` rather than a build-time variable: the UI image is shared across deployments, and
 * the backend is the one that knows which environment it was deployed as.
 *
 * `isLoading` is only ever true while that call is in flight *and* the role alone isn't enough
 * to decide — callers that redirect on `!isInternal` must wait for it, or they bounce internal
 * users away from a deep link before the answer arrives.
 */
export const useIsInternalUser = (): { isInternal: boolean; isLoading: boolean } => {
  const { guardianRole } = useAuth();
  const { data, isLoading } = useHealth();

  const isInternalRole = guardianRole === INTERNAL_ROLE;
  const isDemoEnvironment = data?.environment === DEMO_ENVIRONMENT;

  return {
    isInternal: isInternalRole || isDemoEnvironment,
    isLoading: isLoading && !isInternalRole,
  };
};
